import { Box, CircularProgress } from "@mui/material";
import { type JSX, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useIsAdmin } from "hooks/settings";
import { useStringSerialiser } from "hooks/serialisers/strings";
import { type StringSerialiserOptions } from "hooks/serialisers/types";
import { ValidatedTextField } from "src/components/common/inputs/validatedTextField";
import { useListTenantGroupsQuery } from "src/store/api/generatedApi";

export const TenantGroupRedirect = ({
  basePath,
  fallback,
}: {
  basePath: string;
  fallback: JSX.Element;
}) => {
  const isAdmin = useIsAdmin();
  const { data, isLoading } = useListTenantGroupsQuery();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const tenantGroupNames = data?.tenant_group_names ?? [];
  const singleGroup =
    tenantGroupNames.length === 1 ? tenantGroupNames[0] : undefined;

  if (singleGroup && !isAdmin) {
    return <Navigate to={`${basePath}/${singleGroup}`} replace />;
  }

  if (tenantGroupNames.length === 0 && !isAdmin) {
    return fallback;
  }

  return <TenantGroupInput basePath={basePath} />;
};

const TenantGroupInput = ({ basePath }: { basePath: string }) => {
  const [tenantGroup, setTenantGroup] = useState("");
  const navigate = useNavigate();
  const serialiser = useStringSerialiser();

  return (
    <Box sx={{ p: 4 }}>
      <ValidatedTextField<string, StringSerialiserOptions>
        serialiser={serialiser}
        value={tenantGroup}
        setValue={setTenantGroup}
        label="Tenant group"
        onEditConfirmedWithKeyboard={(group) => {
          void navigate(`${basePath}/${group.toLowerCase()}`, {
            replace: true,
          });
        }}
        textFieldProps={{ autoFocus: true, autoComplete: "off" }}
      />
    </Box>
  );
};
